// WO-7/21 Track A — the Productivity view, built to the canonical design
// (design repo: docs/designs/2026-07-21-migration-daily-aaron-ted-angel/productivity-dashboard.html,
// Angel/ChatGPT "Productivity Tab v3"; overview updated to the Claude Design source
// productivity-tab-v3.dc.html: MiniTeam column in place of Invited/Complete, which still show in
// the drill-down pipeline). Staff-only (same viewAuditTrail gate as the roster):
// an overview of every AE grouped by team with per-preprint + reviewer-pipeline columns and a
// period filter. Opening an AE reuses the AE Productivity v2 page (same Shepherding / closed-out
// / students layout) scoped to that editor. Data comes from GET /dashboard/{staff,ae} (?from/?to).
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { type StaffAeRow, type StaffDashboard } from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { Badge, Card, CardBody, EmptyState, Input, Spinner } from "../../components/ui.js";
import { flagFor } from "../../lib/teams.js";
import { AeProductivity } from "./AeProductivity.js";

const NO_TEAM = "No team assigned";

const fmtDate = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

const initialsOf = (name: string): string => {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? "") + (p[1]?.[0] ?? "")).toUpperCase() || "?";
};

function Avatar({ name }: { name: string }): JSX.Element {
  return (
    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-[11px] font-bold text-indigo-700">
      {initialsOf(name)}
    </span>
  );
}

export function ProductivityPortal(): JSX.Element {
  const [from, setFrom] = useState(() => fmtDate(Date.now() - 28 * 864e5));
  const [to, setTo] = useState(() => fmtDate(Date.now()));
  const [view, setView] = useState<string | null>(null); // AE email when on the detail page

  const weeks = Math.max(0.5, Math.round(((Date.parse(to) - Date.parse(from)) / (7 * 864e5)) * 10) / 10);

  const period = (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-slate-600">Period</span>
      <Input type="date" className="w-36 py-1" value={from} onChange={(e) => setFrom(e.target.value)} />
      <span className="text-slate-400">–</span>
      <Input type="date" className="w-36 py-1" value={to} onChange={(e) => setTo(e.target.value)} />
      <Badge tone="slate">{weeks} weeks</Badge>
    </div>
  );

  return view ? (
    <AeProductivity
      editor={view}
      from={from}
      to={to}
      onFromChange={setFrom}
      onToChange={setTo}
      onBack={() => setView(null)}
    />
  ) : (
    <Overview from={from} to={to} periodControl={period} onOpen={setView} />
  );
}

// ── Overview: every AE, grouped by team ──────────────────────────────────────
function Overview({
  from,
  to,
  periodControl,
  onOpen,
}: {
  from: string;
  to: string;
  periodControl: JSX.Element;
  onOpen: (email: string) => void;
}): JSX.Element {
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const { data, isLoading } = useQuery({
    queryKey: qk.productivityStaff(from, to),
    queryFn: () => api.get<StaffDashboard>(`/dashboard/staff?from=${from}&to=${to}`),
  });

  const buckets = useMemo(() => {
    const byTeam = new Map<string, StaffAeRow[]>();
    for (const ae of data?.aes ?? []) {
      const team = ae.team?.trim() || NO_TEAM;
      byTeam.set(team, [...(byTeam.get(team) ?? []), ae]);
    }
    return [...byTeam.entries()]
      .map(([team, aes]) => ({
        team,
        aes,
        toPitch: aes.reduce((n, a) => n + a.toPitchTeam + a.toPitchEditorial, 0),
        selected: aes.reduce((n, a) => n + a.selected, 0),
        active: aes.reduce((n, a) => n + a.activeCount, 0),
        // Productivity Tab v3: MiniTeam replaces the Invited/Complete columns up here —
        // those stages still show on the AE page's closed-out panel.
        miniTeam: aes.reduce((n, a) => n + (a.miniTeam ?? 0), 0),
        onHold: aes.reduce((n, a) => n + (a.onHold ?? 0), 0),
        dormant: aes.reduce((n, a) => n + (a.dormant ?? 0), 0),
        greenlits: aes.reduce((n, a) => n + (a.greenlitsInPeriod ?? 0), 0),
      }))
      .sort((a, b) => b.greenlits - a.greenlits);
  }, [data]);

  if (isLoading || !data) return <Spinner label="Loading productivity…" />;

  const totGreenlits = buckets.reduce((n, b) => n + b.greenlits, 0);
  const totActive = buckets.reduce((n, b) => n + b.active, 0);
  const totProposals = (data.aes ?? []).reduce((n, a) => n + a.proposals, 0);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Team / AE productivity</h1>
          <p className="mt-0.5 text-sm text-slate-500">How each Assistant Editor is doing — grouped by team. Open any AE to step in.</p>
        </div>
        {periodControl}
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Assistant Editors" value={data.aes.length} sub="Managers in roster" tone="text-amber-700" />
        <Kpi label="Greenlits in period" value={totGreenlits} sub="Reviewer invites across all AEs" tone="text-green-700" />
        <Kpi label="Active preprints" value={totActive} sub="Being shepherded" tone="text-blue-700" />
        <Kpi label="Proposals to date" value={totProposals} sub="On AE-led preprints" tone="text-violet-700" />
      </div>

      {buckets.length === 0 && <EmptyState>No Assistant Editors in the roster yet.</EmptyState>}
      {buckets.map((b) => {
        const open = !closed.has(b.team);
        return (
          <Card key={b.team} className="overflow-hidden">
            <button
              type="button"
              className="flex w-full flex-wrap items-center gap-2.5 border-b border-slate-100 px-4 py-2.5 text-left hover:bg-slate-50"
              aria-expanded={open}
              onClick={() =>
                setClosed((cur) => {
                  const next = new Set(cur);
                  if (next.has(b.team)) next.delete(b.team);
                  else next.add(b.team);
                  return next;
                })
              }
            >
              <span className="text-lg">{flagFor(b.team)}</span>
              <span className="min-w-[8rem]">
                <span className="block text-sm font-semibold">{b.team}</span>
                <span className="block text-xs text-slate-500">
                  {b.aes.length} Assistant Editor{b.aes.length === 1 ? "" : "s"}
                </span>
              </span>
              <span className="flex flex-wrap gap-1.5">
                <Badge tone="blue">To-Pitch {b.toPitch}</Badge>
                <Badge tone="violet">Selected {b.selected}</Badge>
                <Badge tone="blue">Active {b.active}</Badge>
                <Badge tone="violet">MiniTeam {b.miniTeam}</Badge>
                <Badge tone="amber">On Hold {b.onHold}</Badge>
                <Badge tone="slate">Dormant {b.dormant}</Badge>
              </span>
              <span className="ml-auto flex items-center gap-3">
                <span className="text-right">
                  <span className="block text-lg font-bold leading-tight text-green-700">{b.greenlits}</span>
                  <span className="block text-[10px] uppercase tracking-wide text-slate-500">Greenlits</span>
                </span>
                <span className="text-xs text-slate-400">{open ? "▲" : "▼"}</span>
              </span>
            </button>
            {open && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[60rem] border-collapse text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="border-b border-slate-100 px-4 py-2">AE</th>
                      <th className="border-b border-slate-100 px-2 py-2">Domains</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">To-Pitch</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">Selected</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">Active</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">MiniTeam</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">On Hold</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-center">Dormant</th>
                      <th className="border-b border-slate-100 px-2 py-2 text-right">Greenlits</th>
                      <th className="border-b border-slate-100 px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {b.aes.map((ae) => (
                      <tr
                        key={ae.email}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => onOpen(ae.email)}
                      >
                        <td className="border-b border-slate-100 px-4 py-2">
                          <span className="flex items-center gap-2">
                            <Avatar name={ae.name ?? ae.email} />
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium">{ae.name ?? ae.email}</span>
                              <span className="block truncate text-xs text-slate-400">{ae.email}</span>
                            </span>
                          </span>
                        </td>
                        <td className="whitespace-nowrap border-b border-slate-100 px-2 py-2 text-xs text-slate-500">
                          {ae.domains.join(" · ") || "—"}
                        </td>
                        <td className="whitespace-nowrap border-b border-slate-100 px-2 py-2 text-center">
                          <Badge tone="blue">T {ae.toPitchTeam}</Badge>{" "}
                          <Badge tone="amber">E {ae.toPitchEditorial}</Badge>
                        </td>
                        <td className="border-b border-slate-100 px-2 py-2 text-center">{ae.selected}</td>
                        <td className="border-b border-slate-100 px-2 py-2 text-center">{ae.activeCount}</td>
                        <td className="border-b border-slate-100 px-2 py-2 text-center text-violet-700">{ae.miniTeam ?? 0}</td>
                        <td className="border-b border-slate-100 px-2 py-2 text-center text-slate-500">{ae.onHold ?? 0}</td>
                        <td className="border-b border-slate-100 px-2 py-2 text-center text-slate-500">{ae.dormant ?? 0}</td>
                        <td className="border-b border-slate-100 px-2 py-2 text-right font-semibold text-green-700">
                          {ae.greenlitsInPeriod ?? 0}
                        </td>
                        <td className="whitespace-nowrap border-b border-slate-100 px-4 py-2 text-right text-sm font-medium text-brand">
                          Open →
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: number; sub: string; tone: string }): JSX.Element {
  return (
    <Card>
      <CardBody className="space-y-1">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
        <div className={`text-3xl font-bold ${tone}`}>{value}</div>
        <div className="text-xs text-slate-500">{sub}</div>
      </CardBody>
    </Card>
  );
}
